import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Activity, RefreshCw, AlertTriangle, CheckCircle2, Loader2, Clock, Zap } from "lucide-react";
import { JobRetryCell } from "@/components/ingest/JobRetryCell";

/**
 * Monitoring › Ingest Job Manager
 *
 * Surfaces the platform's ingest queue (protocol / CTA / budget /
 * datalake doc / common-knowledge URL ingestion). Tabs separate
 * Active (queued + processing) from Recent (done) and Failed.
 * Auto-refreshes every 4s while the Active tab is open so the
 * operator sees positions move and jobs finish in real-time.
 *
 * The concurrency knob lives on the Settings page
 * (`ingest_queue_concurrency`); this page only shows the live value
 * via the counts endpoint.
 */

const STATUS_COLORS = {
    queued: "bg-blue-400/15 text-blue-300 border-blue-400/40",
    processing: "bg-amber-400/15 text-amber-300 border-amber-400/40",
    done: "bg-emerald-400/15 text-emerald-300 border-emerald-400/40",
    failed: "bg-rose-400/15 text-rose-300 border-rose-400/40",
};

const STATUS_ICONS = {
    queued: Clock,
    processing: Loader2,
    done: CheckCircle2,
    failed: AlertTriangle,
};

const TABS = [
    { id: "active", label: "Active", statuses: ["queued", "processing"] },
    { id: "recent", label: "Recent", statuses: ["done"] },
    { id: "failed", label: "Failed", statuses: ["failed"] },
];

export default function IngestJobsPage() {
    const [tab, setTab] = useState("active");
    const [jobs, setJobs] = useState([]);
    const [counts, setCounts] = useState({});
    const [loading, setLoading] = useState(true);
    const [recoverBusy, setRecoverBusy] = useState(false);

    const fetchAll = useCallback(async () => {
        try {
            const cur = TABS.find((t) => t.id === tab);
            const statuses = cur ? cur.statuses.join(",") : "";
            const [jr, cr] = await Promise.all([
                api.get(`/admin/ingest-jobs?statuses=${statuses}&limit=200`),
                api.get(`/admin/ingest-jobs/counts`),
            ]);
            setJobs(jr.data || []);
            setCounts(cr.data || {});
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    }, [tab]);

    useEffect(() => {
        setLoading(true);
        fetchAll();
    }, [fetchAll]);

    // Auto-refresh on the Active tab — keeps "position in queue" and
    // running counters honest without manual reload. We poll Recent /
    // Failed less aggressively (every 15s) since those are mostly
    // append-only.
    useEffect(() => {
        const interval = tab === "active" ? 4000 : 15000;
        const t = setInterval(fetchAll, interval);
        return () => clearInterval(t);
    }, [tab, fetchAll]);

    const recoverStale = async () => {
        if (!window.confirm("Sweep stale `processing` claims back into `queued`? Run this if a job appears stuck for >30 minutes.")) return;
        setRecoverBusy(true);
        try {
            const { data } = await api.post(`/admin/ingest-jobs/recover-stale`);
            toast.success(`Recovered ${data.recovered} stale claim${data.recovered === 1 ? "" : "s"}`);
            fetchAll();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setRecoverBusy(false);
        }
    };

    return (
        <div className="px-8 py-8" data-testid="ingest-jobs-page">
            <div className="flex items-start justify-between mb-6">
                <div>
                    <div className="label-xs text-[#7C3AED] flex items-center gap-2">
                        <Activity className="w-3.5 h-3.5" strokeWidth={1.8} /> Monitoring
                    </div>
                    <h1 className="font-display text-4xl tracking-tight mt-2">Ingest Job Manager</h1>
                    <p className="text-sm text-white/50 mt-2 max-w-2xl">
                        Every document ingest (protocol · CTA · budget · datalake doc · common-knowledge URL)
                        runs through this queue. The platform processes up to {" "}
                        <span className="font-mono text-[#c7a6ff]">{counts.concurrency ?? "—"}</span> jobs
                        at a time (change the limit in <span className="underline">Settings → Ingest queue concurrency</span>).
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={recoverStale}
                        disabled={recoverBusy}
                        data-testid="ij-recover-stale"
                        title="Sweep stale `processing` claims (>30 min) back to queued"
                        className="border border-amber-500/30 text-amber-200 px-3 py-2 rounded-md text-xs hover:bg-amber-500/10 disabled:opacity-40 flex items-center gap-2"
                    >
                        <AlertTriangle className="w-3.5 h-3.5" strokeWidth={1.8} />
                        Recover stale
                    </button>
                    <button
                        onClick={fetchAll}
                        data-testid="ij-refresh"
                        className="border border-white/15 text-white/75 px-3 py-2 rounded-md text-xs hover:bg-white/5 flex items-center gap-2"
                    >
                        <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.8} /> Refresh
                    </button>
                </div>
            </div>

            {/* Tabs with live counts */}
            <div className="flex border-b border-white/10 mb-4">
                {TABS.map((t) => {
                    const n = t.statuses.reduce((acc, s) => acc + (counts[s] || 0), 0);
                    return (
                        <button
                            key={t.id}
                            onClick={() => setTab(t.id)}
                            data-testid={`ij-tab-${t.id}`}
                            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                                tab === t.id
                                    ? "border-[#7C3AED] text-white"
                                    : "border-transparent text-white/55 hover:text-white"
                            }`}
                        >
                            {t.label}
                            <span className="text-[10px] font-mono text-white/45">·{n}</span>
                        </button>
                    );
                })}
            </div>

            {/* Job list */}
            <div className="rounded-md border border-white/10 overflow-hidden">
                {loading ? (
                    <div className="p-12 text-center text-white/40 text-sm">Loading…</div>
                ) : jobs.length === 0 ? (
                    <div className="p-12 text-center" data-testid="ij-empty">
                        <Zap className="w-10 h-10 text-white/15 mx-auto mb-3" strokeWidth={1.2} />
                        <div className="text-sm text-white/60">No {tab} jobs.</div>
                        {tab === "active" && (
                            <div className="text-[11px] text-white/40 mt-1 font-mono">
                                The queue is idle. Next submission picks up immediately.
                            </div>
                        )}
                    </div>
                ) : (
                    <table className="w-full text-xs" data-testid="ij-table">
                        <thead className="bg-white/[0.03] text-[10px] uppercase tracking-wider text-white/45">
                            <tr>
                                <th className="text-left px-4 py-2">Type</th>
                                <th className="text-left px-4 py-2">Status</th>
                                <th className="text-left px-4 py-2">Filename</th>
                                <th className="text-left px-4 py-2">Tenant</th>
                                <th className="text-left px-4 py-2">Submitted by</th>
                                <th className="text-left px-4 py-2">Queued</th>
                                <th className="text-left px-4 py-2">Result / Error</th>
                                <th className="text-left px-4 py-2">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {jobs.map((j) => {
                                const Icon = STATUS_ICONS[j.status] || Clock;
                                const err = j.error || (j.status === "queued" ? j.last_error : null);
                                const dur = j.finished_at && j.started_at
                                    ? `${((new Date(j.finished_at) - new Date(j.started_at)) / 1000).toFixed(1)}s`
                                    : null;
                                return (
                                    <tr key={j.job_id} className="border-t border-white/5 hover:bg-white/[0.02]"
                                        data-testid={`ij-row-${j.job_id}`}>
                                        <td className="px-4 py-2 font-mono text-white/80">{j.job_type}</td>
                                        <td className="px-4 py-2">
                                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border text-[10px] uppercase tracking-wider ${STATUS_COLORS[j.status] || ""}`}>
                                                <Icon className={`w-3 h-3 ${j.status === "processing" ? "animate-spin" : ""}`} strokeWidth={2} />
                                                {j.status}
                                            </span>
                                            {j.attempts > 1 && (
                                                <div className="text-[10px] text-white/40 font-mono mt-0.5" data-testid={`ij-attempts-${j.job_id}`}>
                                                    {j.attempts} attempts
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-4 py-2 text-white/65 truncate max-w-xs" title={j.filename}>
                                            {j.filename || "—"}
                                            {j.file_size && (
                                                <div className="text-[10px] text-white/35 font-mono">
                                                    {(j.file_size / 1024).toFixed(1)} KB
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-4 py-2 font-mono text-white/55">{j.client_id || "—"}</td>
                                        <td className="px-4 py-2 text-white/55 truncate max-w-[140px]" title={j.actor_email}>
                                            {j.actor_email || "—"}
                                        </td>
                                        <td className="px-4 py-2 font-mono text-white/45 text-[10px]">
                                            {j.queued_at ? new Date(j.queued_at).toLocaleTimeString() : "—"}
                                            {dur && <div className="text-emerald-300/70">took {dur}</div>}
                                        </td>
                                        <td className="px-4 py-2 max-w-md">
                                            {err ? (
                                                <span className="text-rose-300 text-[11px] font-mono" data-testid={`ij-error-${j.job_id}`}>{err.slice(0, 200)}</span>
                                            ) : j.result ? (
                                                <span className="text-emerald-300/85 text-[11px] font-mono">
                                                    {j.result.deduplicated && (
                                                        <span className="block text-sky-200/90 not-italic" data-testid={`ij-dedup-${j.job_id}`}>
                                                            already ingested — existing record kept
                                                        </span>
                                                    )}
                                                    {Object.entries(j.result).filter(([k]) => !k.startsWith("dedup")).slice(0, 3).map(([k, v]) => `${k}=${typeof v === "string" ? v.slice(0, 30) : v}`).join(" · ")}
                                                </span>
                                            ) : (
                                                <span className="text-white/35 text-[11px] font-mono">—</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-2">
                                            <JobRetryCell job={j} onRetried={fetchAll} />
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            <div className="text-[11px] text-white/35 mt-3 font-mono">
                Active jobs auto-refresh every 4s · Done / Failed every 15s · Failed jobs retry themselves (1 min, then every 5 min, 5×) · 7-day retention (then auto-pruned).
            </div>
        </div>
    );
}
